import React from 'react';
import Badge from '@amzn/awsui-components-core/badge';
import Box from '@amzn/awsui-components-core/box';
import Button from '@amzn/awsui-components-core/button';
import FormField from '@amzn/awsui-components-core/form-field';
import Input from '@amzn/awsui-components-core/input';
import Pagination from '@amzn/awsui-components-core/pagination';
import SegmentedControl from '@amzn/awsui-components-core/segmented-control';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import StatusIndicator from '@amzn/awsui-components-core/status-indicator';
import type { StarGridText } from 'src/components/star-grid/hooks/useStarGridText';
import type { DatasetWizardState } from 'src/components/star-grid/datasets/hooks/useDatasetWizard';
import { catalogSources, isS3Uri, pageSources, type SourceMode } from 'src/components/star-grid/datasets/utils/sourceFilter';
import { FixedFiltersEditor } from 'src/components/star-grid/datasets/components/FixedFiltersEditor';
import styles from 'src/components/star-grid/datasets/components/styles/DatasetWizard.module.scss';

/** Only the path's shape is checked here; the service validates the path and role and its `detail` is shown as sent. */
const UriSource: React.FC<{ state: DatasetWizardState; text: StarGridText }> = ({ state, text }) => {
  const uri = state.uriDraft.trim();
  const shaped = isS3Uri(uri);
  const errorText = uri && !shaped ? text('wizardSourceUriInvalid') : state.uriError ?? undefined;
  const detected = state.selectedRef === uri ? state.access?.source : undefined;
  return (
    <SpaceBetween size="s">
      <FormField
        label={text('wizardSourceUriLabel')}
        description={text('wizardSourceUriHint')}
        errorText={errorText}
        secondaryControl={
          <Button
            disabled={!shaped}
            loading={state.uriChecking}
            data-testid="wizard-source-uri-use"
            onClick={() => state.checkTypedSource(uri, state.roleDraft)}
          >
            {text('wizardNext')}
          </Button>
        }
      >
        <Input
          value={state.uriDraft}
          placeholder="s3://bucket/folder/"
          ariaLabel={text('wizardSourceUriLabel')}
          data-testid="wizard-source-uri"
          onChange={({ detail }) => state.setUriDraft(detail.value)}
        />
      </FormField>
      <FormField label={text('wizardSourceRoleLabel')} description={text('wizardSourceRoleHint')}>
        <Input
          value={state.roleDraft}
          placeholder="arn:aws:iam::<account>:role/<name>"
          ariaLabel={text('wizardSourceRoleLabel')}
          data-testid="wizard-source-role"
          onChange={({ detail }) => state.setRoleDraft(detail.value)}
        />
      </FormField>
      {detected && (
        <Box color="text-body-secondary" data-testid="wizard-source-detected">
          {`${text('wizardSourceDetected')}: ${detected.region} · ${detected.format}`}
        </Box>
      )}
    </SpaceBetween>
  );
};

export const WizardSourceStep: React.FC<{ state: DatasetWizardState; text: StarGridText }> = ({ state, text }) => {
  const page = pageSources(catalogSources(state.sources), state.search, state.sourcePage);
  return (
    <div className={styles.body}>
      <p className={styles.help}>{text('wizardSourceHelp')}</p>
      <SegmentedControl
        selectedId={state.sourceMode}
        label={text('wizardSourceHelp')}
        data-testid="wizard-source-mode"
        onChange={({ detail }) => state.setSourceMode(detail.selectedId as SourceMode)}
        options={[
          { id: 'catalog', text: text('wizardSourceModeCatalog') },
          { id: 'uri', text: text('wizardSourceModeUri') }
        ]}
      />
      {state.sourceMode === 'uri' ? (
        <UriSource state={state} text={text} />
      ) : (
        <>
          <SpaceBetween size="xs">
            <FormField label={text('wizardSourceSearch')}>
              <Input
                type="search"
                value={state.search}
                placeholder={text('wizardSourceSearchPlaceholder')}
                ariaLabel={text('wizardSourceSearch')}
                onChange={({ detail }) => state.setSearch(detail.value)}
              />
            </FormField>
            <Box textAlign="right">
              <Button
                variant="inline-link"
                iconName="refresh"
                loading={state.refreshingSources}
                data-testid="wizard-source-refresh"
                onClick={state.refreshSources}
              >
                {text('wizardSourceRefresh')}
              </Button>
            </Box>
          </SpaceBetween>
          {state.sourcesLoading ? (
            <StatusIndicator type="loading" data-testid="wizard-source-loading">
              {text('wizardSourceLoading')}
            </StatusIndicator>
          ) : page.total === 0 ? (
            <Box margin={{ top: 'm' }} color="text-body-secondary">
              {text('wizardSourceEmpty')}
            </Box>
          ) : (
            <>
              <div className={styles.sourceList} role="radiogroup" aria-label={text('wizardSourceHelp')}>
                {page.shown.map((source) => (
                  <button
                    key={source.ref}
                    type="button"
                    role="radio"
                    aria-checked={state.selectedRef === source.ref}
                    data-testid="wizard-source-row"
                    className={`${styles.sourceRow} ${state.selectedRef === source.ref ? styles.sourceRowSelected : ''}`}
                    onClick={() => state.selectSource(source.ref)}
                  >
                    <strong>{source.table}</strong>
                    <span className={styles.sourceRef}>{source.ref}</span>
                    {source.shared_from && <Badge color="grey">{text('wizardSourceShared')}</Badge>}
                  </button>
                ))}
              </div>
              {page.pagesCount > 1 && (
                <Box margin={{ top: 's' }} data-testid="wizard-source-pagination">
                  <Pagination
                    currentPageIndex={page.pageIndex}
                    pagesCount={page.pagesCount}
                    onChange={({ detail }) => state.setSourcePage(detail.currentPageIndex)}
                  />
                </Box>
              )}
            </>
          )}
        </>
      )}
      {state.selectedRef && (
        <Box margin={{ top: 'l' }}>
          {state.sourceColumnsLoading ? (
            <StatusIndicator type="loading" data-testid="wizard-source-columns-loading">
              {text('wizardSourceColumnsLoading')}
            </StatusIndicator>
          ) : (
            <FixedFiltersEditor state={state} text={text} />
          )}
        </Box>
      )}
    </div>
  );
};
